import React, { useCallback, useState } from 'react';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useUserPreferences } from '../../user-preferences';
import { useExampleFeatureFlags } from '../example-feature-flag-context';
import type { ColorMode, FeatureFlagSwitcherConfig, SectionIds } from '../types';

import { useControlledState } from './hooks/use-controlled-state';
import FormSection from './sections';
import ColorModeSection from './sections/color-mode';
import FeatureFlagSwitcherSection from './sections/feature-flag-switcher-section';
import Footer from './sections/footer';

const styles = cssMap({
	container: {
		paddingBlockStart: token('space.250'),
		paddingInlineEnd: token('space.250'),
		paddingBlockEnd: token('space.250'),
		paddingInlineStart: token('space.250'),
	},
	footer: {
		paddingBlockStart: token('space.250'),
		paddingInlineEnd: token('space.250'),
		paddingBlockEnd: token('space.250'),
		paddingInlineStart: token('space.250'),
		backgroundColor: token('elevation.surface.sunken'),
	},
});

type AppearanceControlsFormProps = {
	colorMode?: ColorMode;
	isSynced?: boolean;
	onColorModeChange?(colorMode: ColorMode): void;
	onSyncedChange?(isSynced: boolean): void;
	testId?: string;
	hiddenSections?: SectionIds;
	featureFlagSwitchers?: FeatureFlagSwitcherConfig[];
};

/**
 * __Appearance controls form__
 *
 * The form containing fields for example appearance controls.
 */
const AppearanceControlsForm = ({
	colorMode: controlledColorMode,
	isSynced: controlledIsSynced,
	onColorModeChange,
	onSyncedChange,
	testId,
	featureFlagSwitchers = [],
}: AppearanceControlsFormProps): React.JSX.Element => {
	const { userPreferences, setUserPreferences } = useUserPreferences();
	const exampleFlags = useExampleFeatureFlags();

	const [uncontrolledIsSynced, setUncontrolledIsSynced] = useState<boolean>(
		userPreferences['exampleSyncSettings'] === 'true' || false,
	);
	const isSynced = controlledIsSynced ?? uncontrolledIsSynced;

	const [colorMode, handleColorModeChange] = useControlledState({
		controlledValue: controlledColorMode,
		defaultValue: 'auto',
		preferenceKey: 'exampleColorMode',
		userPreferences,
		setUserPreferences,
		isSynced,
		onChange: onColorModeChange,
	});

	const handleSyncedChange = useCallback(
		(isSynced: boolean) => {
			onSyncedChange?.(isSynced);
			if (!controlledIsSynced) {
				setUncontrolledIsSynced(isSynced);
			}

			if (isSynced) {
				setUserPreferences({
					exampleSyncSettings: 'true',
					exampleColorMode: colorMode,
					exampleFeatureFlags: exampleFlags?.switcherOverrides ?? null,
				});
			} else {
				// Wipe example user preferences
				setUserPreferences({
					exampleSyncSettings: 'false',
					exampleColorMode: null,
					exampleFeatureFlags: null,
				});
			}
		},
		[onSyncedChange, controlledIsSynced, setUserPreferences, colorMode, exampleFlags],
	);

	return (
		<Stack testId={testId}>
			<Stack space="space.250" xcss={styles.container}>
				<FormSection>
					<ColorModeSection
						colorMode={colorMode}
						onColorModeChange={handleColorModeChange}
						testId={testId ? `${testId}--color-mode-section` : undefined}
					/>
				</FormSection>
				{featureFlagSwitchers.length > 0 && (
					<FormSection>
						<Heading size="xsmall" as="h2" id="feature-flags">
							Feature flags
						</Heading>
						{featureFlagSwitchers.map((config) => (
							<FeatureFlagSwitcherSection
								key={config.flagKey}
								config={config}
								testId={testId ? `${testId}--${config.flagKey}` : undefined}
							/>
						))}
					</FormSection>
				)}
			</Stack>
			<Box xcss={styles.footer}>
				<FormSection>
					<Footer
						isSynced={isSynced}
						onSyncedChange={handleSyncedChange}
						testId={testId ? `${testId}--footer` : undefined}
					/>
				</FormSection>
			</Box>
		</Stack>
	);
};

export default AppearanceControlsForm;
